/**
 * PendingTabsTray
 * ----------------
 * Bartender / manager / director-only tray surfacing tabs that need staff
 * action:
 *   - Tabs whose $30 deposit hasn't been collected yet (`deposit_status !== 'paid'`)
 *   - Tabs whose UNPAID balance is high enough to need a top-up
 *
 * Polls /api/tabs/pending-deposits every 8s. Two confirmation buttons per
 * row (Confirm $30 Cash / Confirm $30 Card) — one tap collects the
 * deposit, flips the badge, and removes the row.
 */
import React, { useCallback, useEffect, useState } from 'react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Wallet, AlertTriangle, RefreshCw, Banknote, CreditCard } from 'lucide-react';

const DEPOSIT_AMOUNT = 30;

export default function PendingTabsTray() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState({});

  const fetchRows = useCallback(async () => {
    try {
      const { data } = await api.get('/tabs/pending-deposits');
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      // Silently no-op — bartenders see service errors via the main toast layer
      // already; this tray polls aggressively and we don't want spam.
      console.error('[PendingTabsTray]', err);
    }
  }, []);

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      if (!alive) return;
      setLoading(true);
      try { await fetchRows(); } finally { if (alive) setLoading(false); }
    };
    tick();
    const i = setInterval(tick, 8000);
    return () => { alive = false; clearInterval(i); };
  }, [fetchRows]);

  const confirmPayment = async (tabId, { amount, method, kind }) => {
    const key = `${tabId}:${kind}:${method}`;
    setConfirming((c) => ({ ...c, [key]: true }));
    try {
      await api.post(`/tabs/${tabId}/confirm-payment`, { amount, method, kind });
      toast.success(`${kind === 'deposit' ? 'Deposit' : 'Top-up'} confirmed — $${amount} (${method})`);
      await fetchRows();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not confirm payment');
    } finally {
      setConfirming((c) => ({ ...c, [key]: false }));
    }
  };

  if (rows.length === 0) {
    return null;
  }

  return (
    <div className="bg-white/[0.03] border border-amber-500/30 rounded-sm p-4 space-y-3" data-testid="pending-tabs-tray">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Wallet className="w-4 h-4 text-amber-400" />
          <h3 className="text-sm font-sans text-amber-300 uppercase tracking-wider font-semibold">
            Pending tab payments
          </h3>
          <Badge className="bg-amber-500/20 text-amber-300 border-0 font-sans text-[10px]">
            {rows.length}
          </Badge>
        </div>
        <button
          onClick={fetchRows}
          className="text-zinc-500 hover:text-zinc-300 p-1"
          aria-label="Refresh pending tabs"
          data-testid="pending-tabs-refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>
      <div className="space-y-2">
        {rows.map((tab) => {
          const unpaid = Number(tab.unpaid_balance || 0);
          const needsTopup = !!tab.needs_topup;
          const needsDeposit = !!tab.needs_deposit;
          const topupAmount = Number(tab.topup_minimum || 200);
          return (
            <div
              key={tab.id}
              className="bg-black/30 border border-white/5 rounded-sm p-3 space-y-2"
              data-testid={`pending-tab-row-${tab.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-white text-sm font-sans font-semibold truncate">
                    {tab.customer_name || 'Walk-in guest'}
                  </p>
                  <p className="text-[11px] text-zinc-500 font-sans font-mono">
                    Tab #{(tab.id || '').slice(0, 8).toUpperCase()}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-[10px] text-zinc-500 uppercase font-sans tracking-wider">Unpaid</p>
                  <p className="text-amber-300 font-mono text-sm">${unpaid.toFixed(2)}</p>
                </div>
              </div>
              {needsDeposit && (
                <div className="rounded-sm bg-red-500/10 border border-red-500/30 p-2 space-y-2" data-testid={`pending-tab-deposit-${tab.id}`}>
                  <div className="flex items-center gap-2 text-[11px] text-red-300 font-sans uppercase tracking-wider">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    Deposit pending — ${DEPOSIT_AMOUNT}
                    {tab.deposit_method ? <span className="text-zinc-500 normal-case tracking-normal">· customer said {tab.deposit_method}</span> : null}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      onClick={() => confirmPayment(tab.id, { amount: DEPOSIT_AMOUNT, method: 'cash', kind: 'deposit' })}
                      disabled={!!confirming[`${tab.id}:deposit:cash`]}
                      className="h-8 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-sans rounded-sm"
                      data-testid={`pending-tab-confirm-cash-${tab.id}`}
                    >
                      <Banknote className="w-3.5 h-3.5 mr-1" /> Cash ${DEPOSIT_AMOUNT}
                    </Button>
                    <Button
                      onClick={() => confirmPayment(tab.id, { amount: DEPOSIT_AMOUNT, method: 'card', kind: 'deposit' })}
                      disabled={!!confirming[`${tab.id}:deposit:card`]}
                      className="h-8 bg-blue-accent hover:opacity-90 text-white text-xs font-sans rounded-sm"
                      data-testid={`pending-tab-confirm-card-${tab.id}`}
                    >
                      <CreditCard className="w-3.5 h-3.5 mr-1" /> Card ${DEPOSIT_AMOUNT}
                    </Button>
                  </div>
                </div>
              )}
              {needsTopup && (
                <div className="rounded-sm bg-amber-500/10 border border-amber-500/30 p-2 space-y-2" data-testid={`pending-tab-topup-${tab.id}`}>
                  <div className="flex items-center gap-2 text-[11px] text-amber-300 font-sans uppercase tracking-wider">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    Top-up needed — min ${topupAmount}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      onClick={() => confirmPayment(tab.id, { amount: topupAmount, method: 'cash', kind: 'topup' })}
                      disabled={!!confirming[`${tab.id}:topup:cash`]}
                      className="h-8 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-sans rounded-sm"
                      data-testid={`pending-tab-topup-cash-${tab.id}`}
                    >
                      <Banknote className="w-3.5 h-3.5 mr-1" /> Cash ${topupAmount}
                    </Button>
                    <Button
                      onClick={() => confirmPayment(tab.id, { amount: topupAmount, method: 'card', kind: 'topup' })}
                      disabled={!!confirming[`${tab.id}:topup:card`]}
                      className="h-8 bg-blue-accent hover:opacity-90 text-white text-xs font-sans rounded-sm"
                      data-testid={`pending-tab-topup-card-${tab.id}`}
                    >
                      <CreditCard className="w-3.5 h-3.5 mr-1" /> Card ${topupAmount}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
